import axios from 'axios';
import { toast } from 'sonner';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
  || (import.meta.env.VITE_API_ORIGIN ? `${String(import.meta.env.VITE_API_ORIGIN).replace(/\/+$/, '')}/api/v1` : '/api/v1');

function toSnakeCase(str: string): string {
  return str
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .replace(/([a-z\d])([A-Z])/g, '$1_$2')
    .toLowerCase();
}

function convertKeysToSnakeCase(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (obj instanceof FormData) return obj;
  if (Array.isArray(obj)) return obj.map(convertKeysToSnakeCase);
  if (typeof obj !== 'object') return obj;
  const result: any = {};
  for (const [key, value] of Object.entries(obj)) {
    result[toSnakeCase(key)] = convertKeysToSnakeCase(value);
  }
  return result;
}

const request = axios.create({ baseURL: API_BASE_URL, timeout: 15000 });

let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];
const AUTH_EXPIRED_EVENT = 'hr:auth-expired';
const AUTH_STORAGE_KEYS = [
  'token',
  'role',
  'refresh_token',
  'audit_status',
  'audit_note',
  'user_id',
	  'currentUserId',
	  'need_bindphone',
	  'original_role',
	  'user_name',
	  'user_phone',
	  'current_user_phone',
	];

function onRefreshed(token: string) {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

function onRefreshFailed() {
  refreshSubscribers.forEach(cb => cb(null));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb);
}

function clearAuthStorage() {
  AUTH_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
}

function isAuthExpiredMessage(msg?: string) {
  return Boolean(msg && /(未登录|登录状态|登录已过期|Token已失效|Token无效|token已失效|token无效|账号已被禁用|请重新登录)/i.test(msg));
}

function isAuthExpiredResponse(code: unknown, msg?: string) {
  return Number(code) === 401 || isAuthExpiredMessage(msg);
}

function isPublicAuthRequest(url?: string) {
  if (!url) return false;
  return [
    '/auth/login',
    '/auth/sms-login',
    '/auth/register',
    '/auth/send-sms-code',
    '/auth/verify-code',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/wechat-login',
    '/auth/bind-phone',
  ].some(path => url.includes(path));
}

function redirectToLogin(msg?: string) {
  const now = Date.now();
  const lastRedirect = Number(localStorage.getItem('last_401_redirect') || 0);
  clearAuthStorage();
  if (now - lastRedirect < 1500) return;
  localStorage.setItem('last_401_redirect', now.toString());
  toast.error(msg || '登录状态已失效，请重新登录');
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { msg } }));

  const shouldUseHashLogin = window.location.hash.startsWith('#/') && !window.location.hash.startsWith('#/landing/');
  if (shouldUseHashLogin && window.location.hash !== '#/login') {
    window.location.replace(`${window.location.pathname}${window.location.search}#/login`);
  }
}

// 请求拦截器：注入 JWT token + camelCase→snake_case转换
request.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (config.params && typeof config.params === 'object') {
    config.params = convertKeysToSnakeCase(config.params);
  }
  if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
    config.data = convertKeysToSnakeCase(config.data);
  }
  return config;
});

// 响应拦截器：统一处理 {code, msg, data} + token刷新
request.interceptors.response.use(
  (res) => {
    const { code, msg, data } = res.data;
    if (code !== 0) {
      if (!isPublicAuthRequest(res.config.url) && isAuthExpiredResponse(code, msg)) {
        redirectToLogin(msg);
      } else {
        toast.error(msg || '请求失败');
      }
      const error = new Error(msg || '请求失败') as Error & { response?: typeof res; code?: unknown };
      error.response = res;
      error.code = code;
      return Promise.reject(error);
    }
    return data;
  },
  async (err) => {
    const originalRequest = err.config;
    if (err.response?.status === 401) {
      const msg = err.response?.data?.msg || err.response?.data?.message || '登录状态已失效，请重新登录';
      if (isPublicAuthRequest(originalRequest?.url)) {
        toast.error(msg);
        return Promise.reject(err);
      }
      if (!originalRequest || originalRequest._retry) {
        redirectToLogin(msg);
        return Promise.reject(err);
      }
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        redirectToLogin(msg);
        return Promise.reject(err);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((token) => {
            if (!token) {
              reject(err);
              return;
            }
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(request(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await axios.post(`${API_BASE_URL}/auth/refresh`, { refresh_token: refreshToken });
        const newToken = res.data?.data?.access_token || res.data?.data?.token;
        const newRefreshToken = res.data?.data?.refresh_token;
        if (newToken) {
          localStorage.setItem('token', newToken);
          if (newRefreshToken) localStorage.setItem('refresh_token', newRefreshToken);
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          onRefreshed(newToken);
          return request(originalRequest);
        }
        throw new Error('refresh token invalid');
      } catch (refreshErr: any) {
        onRefreshFailed();
        redirectToLogin(refreshErr?.response?.data?.msg || refreshErr?.response?.data?.message || msg);
      } finally {
        isRefreshing = false;
      }
    } else if (err.response?.status === 403) {
      const msg = err.response?.data?.msg || "无权限操作";
      toast.error(msg);
    } else {
      toast.error(err.response?.data?.msg || '网络错误');
    }
    return Promise.reject(err);
  }
);

export default request;

export interface PageResult<T> {
  list: T[];
  total: number;
  page: number;
  size: number;
}
